// Pending-ack registry — bridges a just-sent message's WhatsApp key back to OUR userMessage._id.
//
// Why: with the fully push-driven ack model, a send is logged `sent_pending_ack` immediately (ack 0)
// and the status "upgrade" to `confirmed` comes naturally from WhatsApp's own `change:ack` push
// (handled in contentMessageServiceHandlers). But that push carries only the WhatsApp message key
// (`message.id._serialized`), not our userMessage._id. The sending engine is the one place that knows
// BOTH (at send time), so we stash the mapping here and resolve it when the ack push arrives — the
// backend then promotes by _id (fast, indexed) with no waMessageKey lookup.
//
// In-memory + bounded. A reload drops it: the message simply stays `sent_pending_ack` (exactly the
// observability signal we want) until retries pick it up. `take` removes on resolve, so each message
// is promoted at most once — later acks (delivered/read) for the same key are no-ops here.

const MAX_ENTRIES = 5000; // FIFO-evict the oldest beyond this (bounds memory on high-volume engines)
const registry = new Map<string, string>(); // waMessageKey._serialized → userMessage._id

/** Record the mapping at send time. Synchronous — runs before any async change:ack callback. */
export function registerPendingAck(waMessageKey: string, userMessageId: string): void {
    if (!waMessageKey || !userMessageId) return;
    if (registry.size >= MAX_ENTRIES) {
        const oldest = registry.keys().next().value;
        if (oldest !== undefined) registry.delete(oldest);
    }
    registry.set(waMessageKey, userMessageId);
}

/** Resolve + remove the mapping when the ack arrives. Returns undefined if not ours / already taken. */
export function takePendingAck(waMessageKey: string): string | undefined {
    if (!waMessageKey) return undefined;
    const id = registry.get(waMessageKey);
    if (id !== undefined) registry.delete(waMessageKey);
    return id;
}
